// https://auth0.com/docs/quickstart/spa/react/01-login
// Modified auth0 react login
// https://auth0.github.io/auth0-spa-js/classes/auth0client.html

import { Auth0Client, Auth0ClientOptions } from '@auth0/auth0-spa-js';
import { AuthClient } from '.';
import { redirectToIdSite } from '~/utils/redirectToIdSite';

export class Auth0ClientWrapper implements AuthClient {
  private client: Auth0Client;

  private targetUrl = '';

  constructor(opts: Auth0ClientOptions) {
    this.client = new Auth0Client(opts);
  }

  async login(): Promise<boolean> {
    // silent login
    try {
      await this.client.getTokenSilently();
    } catch (e) {
      if ((e as { error: string }).error === 'consent_required') {
        const { location } = window;
        // localhost cannot be trusted by auth0
        // this error could happen if you try to register a new user from localhost
        // it can be solved added a confirm popup, but for now let's just add this comment!
        // see https://github.com/auth0/auth0-react/issues/65
        if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
          // eslint-disable-next-line no-console
          console.warn(
            'Localhost is not a verifiable first party app. So we cannot skip the consent form.',
          );
          // eslint-disable-next-line no-console
          console.warn(
            'This affects only localhost when a new user is registred. A quick fix is to login first using a non-local instance.',
          );
        }
      }

      throw e;
    }

    // TODO this is not actually used by Auth0 since the actual authentication happens on ID website
    // check if we are redirected from auth0
    // note: we need also to check the pathname because code can be returned as a result of other oauth flows, e.g. slack
    if (
      window.location.search.includes('code=') &&
      window.location.pathname === '/signin-callback'
    ) {
      const { appState } = await this.client.handleRedirectCallback();
      this.targetUrl = appState.targetUrl
        ? appState.targetUrl
        : import.meta.env.VITE_USER_FRONTPAGE;
    }

    return true;
  }

  redirectToLogin = (returnToUrl: string): Promise<void> => {
    redirectToIdSite(returnToUrl, 'LOGIN');
    return Promise.resolve(undefined);
  };

  isAuthenticated = async (): Promise<boolean> => {
    return this.client.isAuthenticated();
  };

  getTargetUrl = (): string => {
    return this.targetUrl;
  };

  logout = async (params: any): Promise<void> => {
    await this.client.logout(params);
  };

  getTokenSilently = async (...params: any[]) => {
    return this.client.getTokenSilently(...params);
  };
}
